import filter from 'lodash/filter';
import differenceWith from 'lodash/differenceWith';
import isUndefined from 'lodash/isUndefined';
import SHSignals from 'sh-signals';
import isEqual from 'lodash/isEqual';
import forEach from 'lodash/forEach';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {maps as gmaps} from 'google';
import BrandUtils from '@spothero/utils/brand';
import DOMUtils from '@spothero/utils/dom';

class SHGoogleMap extends Component {
    static propTypes = {
        markers: PropTypes.array.isRequired,
        zoom: PropTypes.number.isRequired,
        position: PropTypes.object.isRequired,
        destination: PropTypes.object.isRequired,
        destinationTitle: PropTypes.string.isRequired,
        kmlMapLayerEnabled: PropTypes.bool,
        kmlMapLayerURL: PropTypes.string,
        mapOptions: PropTypes.object.isRequired,
        onDragStart: PropTypes.func.isRequired,
        onDragEnd: PropTypes.func.isRequired,
        onGoogleMapLoaded: PropTypes.func.isRequired,
        onMarkerClick: PropTypes.func.isRequired,
        getMarkerLabelText: PropTypes.func.isRequired,
        selectedSpot: PropTypes.object
    };

    constructor(props) {
        super(props);

        this._zIndexModifiers = {};
        this._zIndexBySpot = {};
        this._MARKER_CONFIG = {
            disabled: {
                default: {
                    icon: {
                        size: new gmaps.Size(12, 15),
                        scaledSize: new gmaps.Size(195, 150),
                        origin: new gmaps.Point(156, 66),
                        labelOrigin: new gmaps.Point(22, 20),
                        anchor: new gmaps.Point(3, 20)
                    },
                    label: ''
                },
                hover: {
                    icon: {
                        size: new gmaps.Size(39, 64),
                        scaledSize: new gmaps.Size(195, 150),
                        origin: new gmaps.Point(156, 0),
                        labelOrigin: new gmaps.Point(20, 20),
                        anchor: new gmaps.Point(16, 54)
                    },
                    label: {
                        color: BrandUtils.getColor('cement'),
                        fontSize: '12px'
                    }
                }
            },
            medium: {
                default: {
                    icon: {
                        size: new gmaps.Size(45, 62),
                        scaledSize: new gmaps.Size(195, 150),
                        origin: new gmaps.Point(0, 88),
                        labelOrigin: new gmaps.Point(22, 20),
                        anchor: new gmaps.Point(19, 55)
                    },
                    label: {
                        color: BrandUtils.getColor('shift'),
                        fontSize: '12px'
                    }
                },
                hover: {
                    icon: {
                        size: new gmaps.Size(45, 71),
                        scaledSize: new gmaps.Size(195, 150),
                        origin: new gmaps.Point(110, 0),
                        labelOrigin: new gmaps.Point(22, 22),
                        anchor: new gmaps.Point(19, 61)
                    },
                    label: {
                        color: BrandUtils.getColor('go'),
                        fontSize: '13px'
                    }
                }
            },
            large: {
                default: {
                    icon: {
                        size: new gmaps.Size(49, 79),
                        scaledSize: new gmaps.Size(195, 150),
                        origin: new gmaps.Point(58, 0),
                        labelOrigin: new gmaps.Point(24, 24),
                        anchor: new gmaps.Point(22, 67)
                    },
                    label: {
                        color: BrandUtils.getColor('go'),
                        fontSize: '14px'
                    }
                },
                hover: {
                    icon: {
                        size: new gmaps.Size(56, 86),
                        scaledSize: new gmaps.Size(195, 150),
                        origin: new gmaps.Point(0, 0),
                        labelOrigin: new gmaps.Point(27, 27),
                        anchor: new gmaps.Point(25, 76)
                    },
                    label: {
                        color: BrandUtils.getColor('go'),
                        fontSize: '15px'
                    }
                }
            }
        };
        this._markers = {};
        this._MARKER_SPRITE_PATH = '/static/main/img/sprites/sprite-markers@2x.png';
        this._HOVER_Z_INDEX = 999999;
    }

    componentDidMount() {
        const {
            zoom,
            position,
            destination,
            destinationTitle,
            kmlMapLayerEnabled,
            kmlMapLayerURL,
            mapOptions,
            onDragStart,
            onDragEnd,
            onGoogleMapLoaded
        } = this.props;

        this._map = new gmaps.Map(DOMUtils.el('.SHGoogleMap'), {
            center: position,
            zoom,
            ...mapOptions
        });

        if (destination && !kmlMapLayerEnabled) {
            this._destination = new gmaps.Marker({
                position,
                map: this._map,
                title: destinationTitle
            });
        }

        if (kmlMapLayerEnabled && kmlMapLayerURL) {
            this._addKML(kmlMapLayerURL);
        }

        gmaps.event.addListenerOnce(this._map, 'idle', () => {
            onGoogleMapLoaded(this._map);
        });
        gmaps.event.addListener(this._map, 'dragstart', onDragStart);
        gmaps.event.addListener(this._map, 'dragend', onDragEnd);

        SHSignals.spotHover.add(this._onSpotHover);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            markers,
            zoom,
            selectedSpot,
            kmlMapLayerEnabled,
            kmlMapLayerURL
        } = this.props;
        const {
            markers: prevMarkers,
            zoom: prevZoom,
            selectedSpot: prevSelectedSpot,
            kmlMapLayerEnabled: prevKmlMapLayerEnabled,
            kmlMapLayerURL: prevKmlMapLayerURL
        } = prevProps;

        if (markers.length !== prevMarkers.length) {
            if (prevMarkers.length < markers.length) {
                forEach(differenceWith(markers, prevMarkers, isEqual), marker => {
                    this._addMarker(marker);
                });
            } else {
                forEach(differenceWith(prevMarkers, markers, isEqual), marker => {
                    this._removeMarker(marker);
                });
            }
        }

        if (zoom !== prevZoom) {
            this._map.setZoom(zoom);
        }

        if ((selectedSpot && !prevSelectedSpot) ||
            (selectedSpot && selectedSpot.spotId !== prevSelectedSpot.spotId)) {

            forEach(filter(this._markers, {markerType: 'large'}), ({spotId, spot, latitude}) => {
                this._toggleActiveMarker(spotId, latitude, false);
            });

            this._toggleActiveMarker(selectedSpot.spotId, selectedSpot.latitude);
        }

        if (prevSelectedSpot && selectedSpot && prevSelectedSpot.spotId !== selectedSpot.spotId) {
            this._toggleActiveMarker(prevSelectedSpot.spotId, prevSelectedSpot.latitude, false);
        }

        if ((kmlMapLayerEnabled && !prevKmlMapLayerEnabled) || (kmlMapLayerURL && !prevKmlMapLayerURL)) {
            this._addKML(kmlMapLayerURL);
        }
    }

    componentWillUnmount() {
        gmaps.event.clearListeners(this._map, 'dragstart');
        gmaps.event.clearListeners(this._map, 'dragend');

        SHSignals.spotHover.remove(this._onSpotHover);

        this._removeAllMarkers();
    }

    _addKML = kmlUrl => {
        if (!this._kml) {
            this._kml = new gmaps.KmlLayer({
                url: kmlUrl,
                map: this._map,
                preserveViewport: true
            });
        }
    }

    _getZIndex = ({spotId, latitude}) => {
        if (!Object.prototype.hasOwnProperty.call(this._zIndexBySpot, spotId)) {
            const zIndex = (Math.abs(latitude)).toFixed(3) * 1000;

            if (Object.prototype.hasOwnProperty.call(this._zIndexModifiers, zIndex)) {
                this._zIndexModifiers[zIndex] += 1;
            } else {
                this._zIndexModifiers[zIndex] = 0;
            }

            this._zIndexBySpot[spotId] = zIndex + this._zIndexModifiers[zIndex];
        }

        return this._zIndexBySpot[spotId];
    }

    _addMarker = marker => {
        const {
            onMarkerClick,
            getMarkerLabelText
        } = this.props;
        const {
            spot,
            state
        } = marker;
        const {
            spotId,
            latitude,
            longitude,
            selectedRate,
            facility,
            title
        } = spot;

        if (!isUndefined(this._markers[spotId])) {
            return this._markers[spotId];
        }

        const isDirectLinkOnly = (facility.status === 'on_direct_link_only');
        const markerType = (selectedRate.unavailable || isDirectLinkOnly)
            ? 'disabled'
            : 'medium';
        const icon = {
            url: this._MARKER_SPRITE_PATH,
            ...this._MARKER_CONFIG[markerType][state].icon
        };
        const zIndex = (state === 'hover')
            ? this._HOVER_Z_INDEX
            : this._getZIndex({
                spotId,
                latitude
            });
        const markerText = getMarkerLabelText({selectedRate, isDirectLinkOnly});
        const labelFontSize = (markerText.length > 5)
            ? `${parseInt(this._MARKER_CONFIG[markerType][state].label.fontSize.replace('px', ''), 10) - 2}px`
            : this._MARKER_CONFIG[markerType][state].label.fontSize;

        this._markers[spotId] = {
            marker: new gmaps.Marker({
                position: new gmaps.LatLng(latitude, longitude),
                map: this._map,
                label: (markerType === 'disabled' && state === 'default')
                    ? ''
                    : {
                        text: markerText,
                        color: this._MARKER_CONFIG[markerType][state].label.color,
                        fontSize: labelFontSize,
                        fontWeight: 'bold',
                        fontFamily: 'Open Sans,sans-serif'
                    },
                icon,
                title,
                zIndex
            }),
            markerType,
            spotId,
            latitude
        };
        this._markers[spotId].marker.addListener('click', () => { onMarkerClick(spotId); });
        this._markers[spotId].marker.addListener('mouseover', () => {
            SHSignals.spotHover.dispatch({
                hovered: true,
                spotId,
                latitude
            });
        });
        this._markers[spotId].marker.addListener('mouseout', () => {
            SHSignals.spotHover.dispatch({
                hovered: false,
                spotId,
                latitude
            });
        });

        return this._markers[spotId];
    }

    _removeMarker = ({spotId}) => {
        if (!isUndefined(this._markers[spotId])) {
            gmaps.event.clearListeners(this._markers[spotId].marker, 'click');
            gmaps.event.clearListeners(this._markers[spotId].marker, 'mouseover');
            gmaps.event.clearListeners(this._markers[spotId].marker, 'mouseout');
            this._markers[spotId].marker.setMap(null);
            delete this._markers[spotId];
        }
    }

    _removeAllMarkers = () => {
        forEach(this._markers, (marker) => {
            this._removeMarker(marker);
        });
    }

    _toggleActiveMarker = (spotId, latitude, active = true) => {
        if (!this._markers[spotId] || this._markers[spotId].markerType === 'disabled') {
            return;
        }

        const markerType = (active) ? 'large' : 'medium';
        const icon = {
            url: this._MARKER_SPRITE_PATH,
            ...this._MARKER_CONFIG[markerType].default.icon
        };
        const label = this._markers[spotId].marker.getLabel();

        label.color = this._MARKER_CONFIG[markerType].default.label.color;
        this._markers[spotId].marker.setLabel(label);
        this._markers[spotId].marker.setIcon(icon);

        if (active) {
            this._markers[spotId].marker.setZIndex(this._HOVER_Z_INDEX - 1);
        } else {
            this._markers[spotId].marker.setZIndex(this._zIndexBySpot[spotId]);
        }

        this._markers[spotId].markerType = markerType;

        gmaps.event.clearListeners(this._markers[spotId].marker, 'mouseover');
        gmaps.event.clearListeners(this._markers[spotId].marker, 'mouseout');

        this._markers[spotId].marker.addListener('mouseover', () => {
            this._onSpotHover({
                hovered: true,
                spotId,
                latitude,
                activeMarker: active
            });
            SHSignals.spotHover.dispatch({
                hovered: true,
                spotId,
                latitude,
                activeMarker: active
            });
        });
        this._markers[spotId].marker.addListener('mouseout', () => {
            SHSignals.spotHover.dispatch({
                hovered: false,
                spotId,
                latitude,
                activeMarker: active
            });
        });
    }

    _onSpotHover = ({hovered, latitude, spotId, activeMarker}) => {
        if (!this._markers[spotId]) {
            return;
        }

        const state = (hovered) ? 'hover' : 'default';
        const zIndex = (hovered)
            ? this._HOVER_Z_INDEX
            : (activeMarker)
                ? this._HOVER_Z_INDEX - 1
                : this._getZIndex({
                    spotId,
                    latitude
                });
        const icon = {
            url: this._MARKER_SPRITE_PATH,
            ...this._MARKER_CONFIG[this._markers[spotId].markerType][state].icon
        };
        const label = this._markers[spotId].marker.getLabel();

        if (this._markers[spotId].markerType !== 'disabled') {
            label.color = this._MARKER_CONFIG[this._markers[spotId].markerType][state].label.color;
            this._markers[spotId].marker.setLabel(label);
        }

        this._markers[spotId].marker.setIcon(icon);

        this._markers[spotId].marker.setZIndex(zIndex);
    }

    render() {
        return (
            <div className="SHGoogleMap" />
        );
    }
}

export default SHGoogleMap;



// WEBPACK FOOTER //
// ./spothero/static/main/js/search/components/common/SHGoogleMap.jsx